import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';

// Zip/file-name safety: attachment names come straight from the field app, so they can
// contain anything. Keep it to characters that survive Windows + macOS unzipping.
function safeName(name, fallback = 'item') {
  const cleaned = String(name ?? '')
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\.+/, '')
    .slice(0, 80);
  return cleaned || fallback;
}

function formatBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// The REST endpoint for a layer, used for the per-feature attachment fallback.
function layerRestUrl(layer) {
  if (layer.parsedUrl?.path) return layer.parsedUrl.path;
  if (layer.url && layer.layerId != null) return `${layer.url}/${layer.layerId}`;
  return layer.url;
}

function withToken(url, token) {
  if (!token) return url;
  return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
}

// Run fn over items with a bounded number of in-flight requests. Attachment downloads
// are one HTTP round trip each and a big area can be hundreds of photos — unbounded
// Promise.all would swamp the browser's connection pool and time out.
async function pooled(items, limit, fn) {
  const out = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i], i);
    }
  });
  await Promise.all(workers);
  return out;
}

// --- Picking the field that says what a feature IS (gas / electric / water) ---------
//
// Every client's schema names this differently, so guess well and let the user correct
// it in the panel. Strongest signal first: an ArcGIS subtype field is literally the
// "what kind of thing is this" field, so prefer it whenever the layer defines one.
const TYPE_FIELD_EXACT = /^(utility|utility_?type|util_?type|linetype|line_?type|point_?type|feature_?type|facility_?type|subtype|sub_?type|type|category|system)$/i;
const TYPE_FIELD_LOOSE = /(util|linetype|line_?type|facility|subtype|type|category|system|material)/i;

// Fields whose values could name a utility. Numeric ones only count when they carry a
// coded-value domain — a bare integer would give us folders called "3".
function groupableFields(layer) {
  const oidField = layer.objectIdField;
  return (layer.fields || []).filter((f) => {
    if (f.name === oidField || f.type === 'oid' || f.type === 'global-id' || f.type === 'guid') return false;
    if (f.type === 'string') return true;
    const numeric = f.type === 'small-integer' || f.type === 'integer';
    return numeric && (f.domain?.type === 'coded-value' || f.name === layer.typeIdField);
  });
}

function guessTypeField(layer) {
  const candidates = groupableFields(layer);
  if (!candidates.length) return '';
  const has = (name) => candidates.find((f) => f.name === name)?.name || '';
  return (
    has(layer.typeIdField) ||
    candidates.find((f) => TYPE_FIELD_EXACT.test(f.name))?.name ||
    candidates.find((f) => TYPE_FIELD_EXACT.test(f.alias || ''))?.name ||
    candidates.find((f) => TYPE_FIELD_LOOSE.test(f.name))?.name ||
    has(layer.displayField) ||
    ''
  );
}

// Turn a stored value into the label a human sees in ArcGIS. Subtype codes resolve via
// layer.types, coded-value domains via the domain — otherwise the raw value is already
// the label. Without this a "Gas" line exports into a folder called "2".
function decodeFieldValue(layer, fieldName, feature) {
  const raw = feature.attributes?.[fieldName];
  if (raw === null || raw === undefined || raw === '') return '';

  if (fieldName === layer.typeIdField && Array.isArray(layer.types)) {
    const t = layer.types.find((x) => String(x.id) === String(raw));
    if (t?.name) return t.name;
  }

  let domain = null;
  try {
    domain = layer.getFieldDomain?.(fieldName, { feature }) || null;
  } catch { /* falls through to the field's own domain */ }
  if (!domain) domain = (layer.fields || []).find((f) => f.name === fieldName)?.domain || null;

  if (domain?.type === 'coded-value') {
    const cv = domain.codedValues?.find((c) => String(c.code) === String(raw));
    if (cv?.name) return cv.name;
  }
  return String(raw);
}

// Quote every cell — attribute values can hold commas, quotes and newlines.
function toCsv(rows) {
  return rows
    .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\r\n');
}

// Fields a user can put in the CSV: everything the service stores except the geometry
// column itself. Coordinates aren't synthesized — the layers carry their own
// Latitude/Longitude attributes, and a computed pair just showed up as duplicates.
function csvFieldChoices(layer) {
  return (layer.fields || [])
    .filter((f) => f.type !== 'geometry')
    .map((f) => ({ name: f.name, alias: f.alias || f.name, type: f.type }));
}
// Default selection: everything except GUIDs, which are noise in a spreadsheet
function defaultCsvFields(choices) {
  return choices.filter((f) => f.type !== 'global-id' && f.type !== 'guid').map((f) => f.name);
}

// One cell: coded values decode to their label, dates become ISO strings (the service
// hands back epoch milliseconds).
function csvCell(layer, field, feature) {
  const raw = feature.attributes?.[field.name];
  if (raw === null || raw === undefined) return '';
  if (field.type === 'date') {
    const d = new Date(raw);
    return isNaN(d) ? String(raw) : d.toISOString();
  }
  return decodeFieldValue(layer, field.name, feature);
}

const chunk = (arr, n) => {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
};

export default function AttachmentExportTool() {
  const [phase, setPhase] = useState('picking'); // picking | map | error
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [webmaps, setWebmaps] = useState([]);
  const [mapTitle, setMapTitle] = useState('');
  const [search, setSearch] = useState('');
  const [hasArea, setHasArea] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total }
  // Attachment-bearing layers inside the drawn area: [{ id, title, geometryType, count, included }]
  // null until an area is drawn, 'counting' while the per-layer counts run.
  const [areaLayers, setAreaLayers] = useState(null);
  // P-number jump: the typed number, a one-line status, and the located project layer
  // ({ pnum, geometryType }) once one is on the map.
  const [pnum, setPnum] = useState('');
  const [projectStatus, setProjectStatus] = useState('');
  const [project, setProject] = useState(null);
  const [locating, setLocating] = useState(false);
  // CSV export: whether the field picker is open, and the chosen field names per layer id.
  // Selections persist for the session so a second export doesn't start from scratch.
  const [csvPickerOpen, setCsvPickerOpen] = useState(false);
  const [csvFields, setCsvFields] = useState({});

  const mapRef = useRef(null);
  const viewRef = useRef(null);
  const webmapRef = useRef(null);
  const attachLayersRef = useRef([]); // feature layers that have attachments enabled
  const graphicsRef = useRef(null);
  const sketchRef = useRef(null);
  const areaRef = useRef(null);
  const tokenRef = useRef(null);
  const projectLayerRef = useRef(null); // the P-number's boundary FeatureLayer, if located

  useEffect(() => {
    loadWebmapList('');
    return () => { try { viewRef.current?.destroy?.(); } catch {} };
  }, []);

  // Search the org's web maps server-side as the user types (debounced)
  useEffect(() => {
    if (phase !== 'picking') return;
    const t = setTimeout(() => { loadWebmapList(search); }, 300);
    return () => clearTimeout(t);
  }, [search, phase]);

  async function loadWebmapList(q) {
    try {
      const { data } = await api.get('/arcgis/webmaps', { params: { q } });
      setWebmaps(data);
    } catch {
      setError('Could not load the web map list. Check ArcGIS access.');
      setPhase('error');
    }
  }

  async function loadMap(item) {
    setPhase('map');
    setMapTitle(item.title);
    setStatus('Loading map…');
    try {
      const { data: tok } = await api.get('/arcgis/portal-token');
      tokenRef.current = tok.token;

      const [
        { default: esriConfig },
        { default: esriId },
        { default: WebMap },
        { default: MapView },
        { default: GraphicsLayer },
        { default: SketchViewModel },
      ] = await Promise.all([
        import('@arcgis/core/config'),
        import('@arcgis/core/identity/IdentityManager'),
        import('@arcgis/core/WebMap'),
        import('@arcgis/core/views/MapView'),
        import('@arcgis/core/layers/GraphicsLayer'),
        import('@arcgis/core/widgets/Sketch/SketchViewModel'),
      ]);

      esriConfig.portalUrl = tok.orgUrl;
      esriId.registerToken({ server: `${tok.orgUrl}/sharing/rest`, token: tok.token, ssl: true });

      const webmap = new WebMap({ portalItem: { id: item.id } });
      const view = new MapView({ container: mapRef.current, map: webmap });
      viewRef.current = view;
      webmapRef.current = webmap;
      await view.when();
      await webmap.loadAll().catch(() => {});

      // Keep only feature layers that actually have attachments — everything else
      // would just be an empty folder in the zip. Includes layers nested in groups.
      attachLayersRef.current = webmap.allLayers
        .filter((l) => l.type === 'feature' && l.capabilities?.data?.supportsAttachment)
        .toArray();

      const gl = new GraphicsLayer({ listMode: 'hide' });
      webmap.add(gl);
      graphicsRef.current = gl;
      const svm = new SketchViewModel({ view, layer: gl });
      svm.on('create', (e) => {
        if (e.state === 'complete') {
          areaRef.current = e.graphic.geometry;
          setHasArea(true);
          analyzeArea(e.graphic.geometry);
        }
      });
      sketchRef.current = svm;

      setStatus(
        attachLayersRef.current.length
          ? ''
          : 'No layers in this map have attachments enabled.'
      );
    } catch (e) {
      console.error(e);
      setError('Failed to load the map.');
      setPhase('error');
    }
  }

  function backToPicker() {
    try { viewRef.current?.destroy?.(); } catch {}
    viewRef.current = null;
    webmapRef.current = null;
    attachLayersRef.current = [];
    graphicsRef.current = null;
    sketchRef.current = null;
    areaRef.current = null;
    tokenRef.current = null;
    projectLayerRef.current = null;
    setHasArea(false);
    setAreaLayers(null);
    setProgress(null);
    setStatus('');
    setMapTitle('');
    setSearch('');
    setPnum('');
    setProjectStatus('');
    setProject(null);
    setLocating(false);
    setCsvPickerOpen(false);
    setCsvFields({});
    setPhase('picking');
    loadWebmapList('');
  }

  // Jump the map to a job by its P-number. Each project has its own boundary Feature
  // Service in the org (found the same way the View Map pop-up finds it); add it as an
  // outlined overlay and zoom to it so the user can draw the area right where the work is.
  async function locateProject(e) {
    e?.preventDefault?.();
    const raw = pnum.trim();
    const view = viewRef.current;
    const webmap = webmapRef.current;
    if (!raw || !view || !webmap || locating) return;
    setLocating(true);
    setProjectStatus('Finding project…');
    setProject(null);
    try {
      const { data: found } = await api.get(`/arcgis/project-layer/${encodeURIComponent(raw)}`);
      const [{ default: esriId }, { default: FeatureLayer }] = await Promise.all([
        import('@arcgis/core/identity/IdentityManager'),
        import('@arcgis/core/layers/FeatureLayer'),
      ]);
      // The boundary service lives on its own hosted server, not the portal — register
      // the token there too or the layer 499s.
      const svcRoot = found.url.split('/rest/')[0] + '/rest/services';
      esriId.registerToken({ server: svcRoot, token: tokenRef.current, ssl: true });

      if (projectLayerRef.current) {
        try { webmap.remove(projectLayerRef.current); } catch {}
        projectLayerRef.current = null;
      }
      const fl = new FeatureLayer({
        url: `${found.url}/0`,
        outFields: ['*'],
        title: `Project ${found.title}`,
        listMode: 'hide',
        popupEnabled: false,
      });
      webmap.add(fl);
      await fl.when();

      // Gold outline so the boundary reads over any basemap / client symbology
      const gold = [201, 168, 76];
      if (fl.geometryType === 'polygon') {
        fl.renderer = {
          type: 'simple',
          symbol: { type: 'simple-fill', color: [...gold, 0.08], outline: { color: gold, width: 2.5 } },
        };
      } else if (fl.geometryType === 'polyline') {
        fl.renderer = { type: 'simple', symbol: { type: 'simple-line', color: gold, width: 3 } };
      } else {
        fl.renderer = {
          type: 'simple',
          symbol: { type: 'simple-marker', color: gold, size: 12, outline: { color: [13, 27, 46], width: 1.5 } },
        };
      }
      projectLayerRef.current = fl;

      const res = await fl.queryExtent();
      const ext = res?.extent;
      if (!ext || !res.count) {
        setProjectStatus(`${found.title} was found but has no features to zoom to.`);
        setProject({ pnum: found.title, geometryType: fl.geometryType, count: 0 });
        return;
      }
      if (ext.width === 0 && ext.height === 0) {
        await view.goTo({ target: ext.center, zoom: 17 }); // a single point has no area to fit
      } else {
        await view.goTo(ext.expand(1.6));
      }
      setProject({ pnum: found.title, geometryType: fl.geometryType, count: res.count });
      setProjectStatus('');
    } catch (err) {
      console.warn('P-number lookup failed:', err);
      setProjectStatus(
        err?.response?.status === 404
          ? `No feature layer found for ${raw}.`
          : `Could not locate ${raw}.`
      );
    } finally {
      setLocating(false);
    }
  }

  // Use the located project's boundary as the export area instead of hand-drawing one.
  // Several boundary polygons (phases, parcels) union into a single area.
  async function useProjectBoundary() {
    const fl = projectLayerRef.current;
    const view = viewRef.current;
    if (!fl || !view || !graphicsRef.current || exporting) return;
    setProjectStatus('Reading boundary…');
    try {
      const [geometryEngine, { default: Graphic }] = await Promise.all([
        import('@arcgis/core/geometry/geometryEngine'),
        import('@arcgis/core/Graphic'),
      ]);
      const q = fl.createQuery();
      q.where = '1=1';
      q.returnGeometry = true;
      q.outFields = [fl.objectIdField];
      q.outSpatialReference = view.spatialReference;
      const fs = await fl.queryFeatures(q);
      const geoms = fs.features.map((f) => f.geometry).filter(Boolean);
      if (!geoms.length) {
        setProjectStatus('The project boundary has no shapes to use.');
        return;
      }
      const area = geoms.length === 1 ? geoms[0] : geometryEngine.union(geoms);
      try { sketchRef.current?.cancel?.(); } catch {}
      graphicsRef.current.removeAll();
      graphicsRef.current.add(new Graphic({
        geometry: area,
        symbol: { type: 'simple-fill', color: [150, 150, 150, 0.2], outline: { color: [255, 255, 255], width: 2 } },
      }));
      areaRef.current = area;
      setHasArea(true);
      setProgress(null);
      setProjectStatus('');
      analyzeArea(area);
    } catch (err) {
      console.warn('Boundary read failed:', err);
      setProjectStatus('Could not read the project boundary.');
    }
  }

  function startDraw(tool) {
    if (!sketchRef.current) return;
    graphicsRef.current.removeAll();
    areaRef.current = null;
    setHasArea(false);
    setAreaLayers(null);
    setProgress(null);
    sketchRef.current.create(tool);
  }

  // Count each attachment-bearing layer's features inside the drawn area so the panel
  // only lists layers that would contribute. The ask is points and lines, so those are
  // checked by default; polygon layers are listed but off unless the user wants them.
  async function analyzeArea(area) {
    setAreaLayers('counting');
    const counts = await Promise.all(
      attachLayersRef.current.map(async (layer) => {
        try {
          const q = layer.createQuery();
          q.geometry = area;
          q.spatialRelationship = 'intersects';
          const count = await layer.queryFeatureCount(q);
          const gt = layer.geometryType;
          return {
            id: layer.id,
            title: layer.title || `layer_${layer.id}`,
            geometryType: gt,
            count,
            included: gt === 'point' || gt === 'multipoint' || gt === 'polyline',
            // Which attribute becomes the folder name, plus the choices for the override
            groupField: guessTypeField(layer),
            fieldChoices: groupableFields(layer).map((f) => ({ name: f.name, alias: f.alias || f.name })),
          };
        } catch {
          return null; // count failed — leave it out of the panel
        }
      })
    );
    // Ignore a stale result if the user has drawn a newer area meanwhile
    if (areaRef.current === area) {
      setAreaLayers(counts.filter((l) => l && l.count > 0));
    }
  }

  function toggleLayer(id) {
    setAreaLayers((prev) =>
      Array.isArray(prev) ? prev.map((l) => (l.id === id ? { ...l, included: !l.included } : l)) : prev
    );
  }

  function setLayerGroupField(id, groupField) {
    setAreaLayers((prev) =>
      Array.isArray(prev) ? prev.map((l) => (l.id === id ? { ...l, groupField } : l)) : prev
    );
  }

  // Attachment metadata for a set of object IDs. Prefers the batched queryAttachments
  // operation; older services only expose the per-feature endpoint, so fall back to that.
  async function fetchAttachmentInfos(layer, objectIds) {
    const token = tokenRef.current;
    const byOid = {};

    if (layer.capabilities?.operations?.supportsQueryAttachments) {
      try {
        for (const ids of chunk(objectIds, 100)) {
          const res = await layer.queryAttachments({ objectIds: ids });
          for (const [oid, infos] of Object.entries(res || {})) {
            byOid[oid] = (infos || []).map((a) => ({
              id: a.id,
              name: a.name,
              size: a.size,
              contentType: a.contentType,
              url: a.url || `${layerRestUrl(layer)}/${oid}/attachments/${a.id}`,
            }));
          }
        }
        return byOid;
      } catch (e) {
        console.warn('queryAttachments failed, falling back per-feature:', layer.title, e);
      }
    }

    const base = layerRestUrl(layer);
    await pooled(objectIds, 6, async (oid) => {
      try {
        const res = await fetch(withToken(`${base}/${oid}/attachments?f=json`, token));
        const data = await res.json();
        byOid[oid] = (data.attachmentInfos || []).map((a) => ({
          id: a.id,
          name: a.name,
          size: a.size,
          contentType: a.contentType,
          url: `${base}/${oid}/attachments/${a.id}`,
        }));
      } catch (e) {
        console.warn('Attachment lookup failed for OID', oid, e);
      }
    });
    return byOid;
  }

  async function doExport() {
    if (!areaRef.current) return;
    setExporting(true);
    setProgress(null);
    try {
      const { default: JSZip } = await import('jszip');
      const token = tokenRef.current;
      const area = areaRef.current;

      const included = new Set(
        Array.isArray(areaLayers) ? areaLayers.filter((l) => l.included).map((l) => l.id) : []
      );
      const layers = attachLayersRef.current.filter((l) => included.has(l.id));
      const groupFieldById = new Map(
        (Array.isArray(areaLayers) ? areaLayers : []).map((l) => [l.id, l.groupField])
      );
      if (!layers.length) {
        alert('Pick at least one layer to export.');
        return;
      }

      // Pass 1 — find the features in the area and their attachment metadata, so the
      // total is known before a single byte is downloaded.
      setStatus('Finding attachments…');
      const plans = [];
      for (const layer of layers) {
        try {
          const oidField = layer.objectIdField;
          const groupField = groupFieldById.get(layer.id) || '';
          const q = layer.createQuery();
          q.geometry = area;
          q.spatialRelationship = 'intersects';

          // queryObjectIds, not queryFeatures: the returnIdsOnly endpoint isn't subject to
          // the service's maxRecordCount, so a dense area can't silently drop features.
          const oids = (await layer.queryObjectIds(q)) || [];
          if (!oids.length) continue;

          // What each feature IS — gas, electric, water — which becomes its folder.
          // Read in sub-maxRecordCount batches and decoded through subtypes/domains.
          const groups = {};
          if (groupField) {
            for (const ids of chunk(oids, 500)) {
              try {
                const q2 = layer.createQuery();
                q2.objectIds = ids;
                q2.returnGeometry = false;
                q2.outFields = [oidField, groupField];
                const fs = await layer.queryFeatures(q2);
                for (const f of fs.features) {
                  const label = decodeFieldValue(layer, groupField, f);
                  if (label) groups[f.attributes[oidField]] = label;
                }
              } catch (e) {
                console.warn('Type lookup failed:', layer.title, e);
              }
            }
          }

          const infos = await fetchAttachmentInfos(layer, oids);
          for (const [oid, list] of Object.entries(infos)) {
            for (const a of list || []) {
              plans.push({
                layerTitle: layer.title || `layer_${layer.id}`,
                geometryType: layer.geometryType,
                oid,
                // No type field chosen -> no subfolder at all, just the layer folder
                group: groupField ? (groups[oid] || 'Unspecified') : '',
                ...a,
              });
            }
          }
        } catch (e) {
          console.warn('Layer scan failed:', layer.title, e);
        }
      }

      if (!plans.length) {
        alert('No attachments found on the features in that area.');
        return;
      }

      // Assign every zip path up front, in a fixed order. Doing it here rather than
      // inside the concurrent download pass keeps names stable run to run — otherwise
      // whichever photo happened to land first would win the un-suffixed name.
      plans.sort(
        (a, b) =>
          String(a.layerTitle).localeCompare(String(b.layerTitle)) ||
          String(a.group).localeCompare(String(b.group)) ||
          Number(a.oid) - Number(b.oid) ||
          Number(a.id) - Number(b.id)
      );
      const usedPaths = new Set();
      for (const p of plans) {
        const layerFolder = safeName(p.layerTitle, 'layer');
        const folder = p.group ? `${layerFolder}/${safeName(p.group, 'Unspecified')}` : layerFolder;
        const file = safeName(p.name, `attachment_${p.id}`);
        const dot = file.lastIndexOf('.');
        const stem = dot > 0 ? file.slice(0, dot) : file;
        const ext = dot > 0 ? file.slice(dot) : '';
        // Many features now share a folder, so same-named photos are likely. Fall back to
        // the object ID, then the attachment ID — that pair is unique by definition.
        let path = `${folder}/${file}`;
        if (usedPaths.has(path.toLowerCase())) path = `${folder}/${stem}_${p.oid}${ext}`;
        if (usedPaths.has(path.toLowerCase())) path = `${folder}/${stem}_${p.oid}_${p.id}${ext}`;
        usedPaths.add(path.toLowerCase());
        p.path = path;
      }

      const totalBytes = plans.reduce((s, p) => s + (p.size || 0), 0);
      const ok = window.confirm(
        `${plans.length} attachment${plans.length === 1 ? '' : 's'} (${formatBytes(totalBytes)}) across ${layers.length} layer${layers.length === 1 ? '' : 's'}.\n\nDownload and zip them?`
      );
      if (!ok) return;

      // Pass 2 — download the files
      setStatus('');
      setProgress({ done: 0, total: plans.length });
      let done = 0;
      const zip = new JSZip();
      const manifest = [['Layer', 'Geometry', 'Utility Type', 'ObjectID', 'File', 'Zip Path', 'Bytes', 'Content Type', 'Status']];

      await pooled(plans, 6, async (p) => {
        let statusText = 'ok';
        try {
          const res = await fetch(withToken(p.url, token));
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          zip.file(p.path, await res.blob());
        } catch (e) {
          console.warn('Attachment download failed:', p.path, e);
          statusText = `failed: ${e.message}`;
        }
        manifest.push([p.layerTitle, p.geometryType, p.group, p.oid, p.name, p.path, p.size ?? '', p.contentType ?? '', statusText]);
        done += 1;
        setProgress({ done, total: plans.length });
      });

      const failed = manifest.slice(1).filter((r) => r[8] !== 'ok').length;
      // Rows land in download-completion order; group them back by layer, then type
      const header = manifest[0];
      const rows = manifest.slice(1).sort(
        (x, y) =>
          String(x[0]).localeCompare(String(y[0])) ||
          String(x[2]).localeCompare(String(y[2])) ||
          Number(x[3]) - Number(y[3])
      );
      zip.file('manifest.csv', toCsv([header, ...rows]));

      setStatus('Zipping…');
      const blob = await zip.generateAsync({ type: 'blob' }); // photos are already compressed
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName(mapTitle, 'map')}_attachments.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setStatus(failed ? `Done — ${failed} attachment(s) failed; see manifest.csv.` : '');
    } catch (e) {
      console.error(e);
      alert('Export failed. See console for details.');
    } finally {
      setExporting(false);
      setProgress(null);
    }
  }

  // Layers ticked in the panel, paired with their live FeatureLayer objects
  function includedLayers() {
    const included = new Set(
      Array.isArray(areaLayers) ? areaLayers.filter((l) => l.included).map((l) => l.id) : []
    );
    return attachLayersRef.current.filter((l) => included.has(l.id));
  }

  function openCsvPicker() {
    const layers = includedLayers();
    if (!layers.length) {
      alert('Pick at least one layer to export.');
      return;
    }
    // First time a layer shows up in the picker, start it with the default field set
    setCsvFields((prev) => {
      const next = { ...prev };
      for (const l of layers) {
        if (!next[l.id]) next[l.id] = defaultCsvFields(csvFieldChoices(l));
      }
      return next;
    });
    setCsvPickerOpen(true);
  }

  function toggleCsvField(layerId, name) {
    setCsvFields((prev) => {
      const cur = prev[layerId] || [];
      return { ...prev, [layerId]: cur.includes(name) ? cur.filter((n) => n !== name) : [...cur, name] };
    });
  }

  function setAllCsvFields(layer, on) {
    setCsvFields((prev) => ({ ...prev, [layer.id]: on ? csvFieldChoices(layer).map((f) => f.name) : [] }));
  }

  // Rows for the features inside the area, one CSV per layer (schemas differ), zipped
  // when there's more than one so the download is still a single file.
  async function doCsvExport() {
    if (!areaRef.current) return;
    const layers = includedLayers().filter((l) => (csvFields[l.id] || []).length);
    if (!layers.length) {
      alert('Pick at least one field to include.');
      return;
    }
    setCsvPickerOpen(false);
    setExporting(true);
    setProgress(null);
    try {
      const area = areaRef.current;
      const files = []; // { name, text, rows }
      setStatus('Reading features…');

      for (const layer of layers) {
        const choices = csvFieldChoices(layer);
        const chosen = new Set(csvFields[layer.id]);
        // Keep the service's field order, not click order — it matches what people see in ArcGIS
        const fields = choices.filter((f) => chosen.has(f.name));
        const outFields = fields.map((f) => f.name);
        const oidField = layer.objectIdField;

        const q = layer.createQuery();
        q.geometry = area;
        q.spatialRelationship = 'intersects';
        const oids = (await layer.queryObjectIds(q)) || [];
        const rows = [fields.map((f) => f.alias)];
        // Read in sub-maxRecordCount batches — same reason as the attachment scan
        for (const ids of chunk(oids, 500)) {
          const q2 = layer.createQuery();
          q2.objectIds = ids;
          q2.outFields = Array.from(new Set([oidField, ...outFields]));
          q2.returnGeometry = false;
          q2.orderByFields = [oidField];
          const fs = await layer.queryFeatures(q2);
          for (const f of fs.features) rows.push(fields.map((fld) => csvCell(layer, fld, f)));
        }
        files.push({ name: `${safeName(layer.title || `layer_${layer.id}`, 'layer')}.csv`, text: toCsv(rows), rows: rows.length - 1 });
      }

      const total = files.reduce((n, f) => n + f.rows, 0);
      if (!total) {
        alert('No features from the chosen layers fall inside that area.');
        return;
      }

      let blob;
      let filename;
      if (files.length === 1) {
        blob = new Blob([files[0].text], { type: 'text/csv;charset=utf-8' });
        filename = `${safeName(mapTitle, 'map')}_${files[0].name}`;
      } else {
        const { default: JSZip } = await import('jszip');
        const zip = new JSZip();
        const used = new Set();
        for (const f of files) {
          let name = f.name;
          if (used.has(name.toLowerCase())) name = name.replace(/\.csv$/, `_${used.size + 1}.csv`);
          used.add(name.toLowerCase());
          zip.file(name, f.text);
        }
        blob = await zip.generateAsync({ type: 'blob' });
        filename = `${safeName(mapTitle, 'map')}_csv.zip`;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setStatus(`CSV: ${total} row${total === 1 ? '' : 's'} across ${files.length} layer${files.length === 1 ? '' : 's'}.`);
    } catch (e) {
      console.error(e);
      alert('CSV export failed. See console for details.');
    } finally {
      setExporting(false);
    }
  }

  const noLayersReady = areaLayers === 'counting' || (Array.isArray(areaLayers) && !areaLayers.some((l) => l.included));

  if (phase === 'error') {
    return <p className="text-red-400 text-sm">{error}</p>;
  }

  if (phase === 'picking') {
    return (
      <div className="max-w-md space-y-3">
        <p className="text-gray-400 text-sm">
          Pick a web map, jump to the job by P-number, draw an area around it, and download the
          photos attached to the points and lines inside it.
        </p>
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search web maps… (e.g. millman)"
          className="w-full rounded px-3 py-2 text-sm text-white border focus:outline-none"
          style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
        />
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {webmaps.length === 0 ? (
            <p className="text-gray-500 text-xs italic">No maps match.</p>
          ) : webmaps.map((m) => (
            <button
              key={m.id}
              onClick={() => loadMap(m)}
              className="w-full text-left px-3 py-2 rounded border text-sm text-gray-200 transition hover:border-gold-500"
              style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
            >
              {m.title}
              <span className="block text-xs text-gray-500">{m.owner}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-gray-400 text-sm">
          <span className="text-white font-bold">{mapTitle}</span>
          {status && <span> · {status}</span>}
          {progress && <span> · Downloading {progress.done} / {progress.total}</span>}
        </p>
        <button
          onClick={backToPicker}
          disabled={exporting}
          className="px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wide disabled:opacity-40"
          style={{ backgroundColor: '#0d1b2e', color: '#9ca3af', border: '1px solid #1e3a5f' }}
        >
          ⇄ Change Map
        </button>
      </div>

      <form onSubmit={locateProject} className="flex items-center gap-2 flex-wrap">
        <input
          value={pnum}
          onChange={(e) => setPnum(e.target.value)}
          placeholder="P-number… (e.g. P-1234)"
          disabled={exporting}
          className="rounded px-3 py-1.5 text-sm text-white border focus:outline-none disabled:opacity-40"
          style={{ backgroundColor: '#142236', borderColor: '#1e3a5f', width: '14rem' }}
        />
        <button
          type="submit"
          disabled={!pnum.trim() || locating || exporting}
          className="px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wide disabled:opacity-40"
          style={{ backgroundColor: '#0d1b2e', color: '#c9a84c', border: '1px solid #c9a84c' }}
        >
          {locating ? 'Finding…' : '⌖ Go to P#'}
        </button>
        {project && project.geometryType === 'polygon' && project.count > 0 && (
          <button
            type="button"
            onClick={useProjectBoundary}
            disabled={exporting}
            className="px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wide disabled:opacity-40"
            style={{ backgroundColor: '#0d1b2e', color: '#9ca3af', border: '1px solid #1e3a5f' }}
            title="Use the project boundary as the export area instead of drawing one"
          >
            ▣ Use Boundary as Area
          </button>
        )}
        {project && !projectStatus && (
          <span className="text-xs text-gray-400">
            Showing <span className="text-gold-500 font-bold">{project.pnum}</span> in gold
          </span>
        )}
        {projectStatus && <span className="text-xs text-gray-400">{projectStatus}</span>}
      </form>

      <div className="relative rounded-xl border overflow-hidden" style={{ borderColor: '#1e3a5f', height: '70vh' }}>
        <div ref={mapRef} className="absolute inset-0" />

        <div className="absolute top-3 left-3 z-10 flex gap-2 flex-wrap">
          <button
            onClick={() => startDraw('polygon')}
            disabled={exporting}
            className="px-3 py-2 rounded text-xs font-bold uppercase tracking-wide shadow-lg disabled:opacity-40"
            style={{ backgroundColor: '#0d1b2e', color: '#c9a84c', border: '1px solid #c9a84c' }}
          >
            ⬠ Draw Polygon
          </button>
          <button
            onClick={() => startDraw('rectangle')}
            disabled={exporting}
            className="px-3 py-2 rounded text-xs font-bold uppercase tracking-wide shadow-lg disabled:opacity-40"
            style={{ backgroundColor: '#0d1b2e', color: '#9ca3af', border: '1px solid #1e3a5f' }}
          >
            ▭ Rectangle
          </button>
          <button
            onClick={doExport}
            disabled={!hasArea || exporting || noLayersReady}
            className="px-3 py-2 rounded text-xs font-bold uppercase tracking-wide shadow-lg disabled:opacity-40"
            style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
          >
            {exporting ? 'Exporting…' : '⤓ Export Attachments'}
          </button>
          <button
            onClick={openCsvPicker}
            disabled={!hasArea || exporting || noLayersReady}
            className="px-3 py-2 rounded text-xs font-bold uppercase tracking-wide shadow-lg disabled:opacity-40"
            style={{ backgroundColor: '#0d1b2e', color: '#c9a84c', border: '1px solid #c9a84c' }}
          >
            ▤ Export CSV
          </button>
        </div>

        {csvPickerOpen && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
            onClick={() => setCsvPickerOpen(false)}
          >
            <div
              className="rounded-xl shadow-2xl flex flex-col w-full"
              style={{ backgroundColor: '#0d1b2e', border: '1px solid #1e3a5f', maxWidth: '44rem', maxHeight: '100%' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: '#1e3a5f' }}>
                <div>
                  <p className="font-bold uppercase tracking-wide text-sm" style={{ color: '#c9a84c' }}>Export CSV — choose fields</p>
                  <p className="text-xs text-gray-500">One CSV per layer for the features inside the area. Codes are written as their labels.</p>
                </div>
                <button onClick={() => setCsvPickerOpen(false)} className="text-gray-500 hover:text-white text-lg leading-none px-1">×</button>
              </div>

              <div className="overflow-y-auto px-4 py-3 space-y-4 text-xs">
                {includedLayers().map((layer) => {
                  const choices = csvFieldChoices(layer);
                  const chosen = new Set(csvFields[layer.id] || []);
                  return (
                    <div key={layer.id}>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-gray-200 font-bold truncate" title={layer.title}>
                          {layer.title}
                          <span className="ml-2 font-normal text-gray-500">{chosen.size} / {choices.length} fields</span>
                        </p>
                        <div className="flex gap-2 shrink-0">
                          <button type="button" onClick={() => setAllCsvFields(layer, true)} className="text-gray-400 hover:text-white uppercase tracking-wide">All</button>
                          <button type="button" onClick={() => setAllCsvFields(layer, false)} className="text-gray-400 hover:text-white uppercase tracking-wide">None</button>
                        </div>
                      </div>
                      <div className="grid gap-x-3 gap-y-1" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(12rem, 1fr))' }}>
                        {choices.map((f) => (
                          <label key={f.name} className="flex items-center gap-2 cursor-pointer text-gray-300 min-w-0">
                            <input
                              type="checkbox"
                              checked={chosen.has(f.name)}
                              onChange={() => toggleCsvField(layer.id, f.name)}
                              className="accent-gold-500 shrink-0"
                            />
                            <span className="truncate" title={f.name}>
                              {f.alias}
                              {f.alias !== f.name && (
                                <span className="text-gray-600"> · {f.name}</span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="px-4 py-3 border-t flex justify-end gap-2" style={{ borderColor: '#1e3a5f' }}>
                <button
                  type="button"
                  onClick={() => setCsvPickerOpen(false)}
                  className="px-3 py-2 rounded text-xs font-bold uppercase tracking-wide"
                  style={{ backgroundColor: '#142236', color: '#9ca3af', border: '1px solid #1e3a5f' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={doCsvExport}
                  disabled={!includedLayers().some((l) => (csvFields[l.id] || []).length)}
                  className="px-3 py-2 rounded text-xs font-bold uppercase tracking-wide disabled:opacity-40"
                  style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
                >
                  ⤓ Download CSV
                </button>
              </div>
            </div>
          </div>
        )}

        {areaLayers !== null && (
          <div
            className="absolute top-3 right-3 z-10 rounded shadow-lg text-xs"
            style={{ backgroundColor: '#0d1b2e', border: '1px solid #1e3a5f', width: '16rem', maxHeight: '60%', overflowY: 'auto' }}
          >
            <p className="px-3 py-2 font-bold uppercase tracking-wide border-b" style={{ color: '#c9a84c', borderColor: '#1e3a5f' }}>
              Layers in Area
            </p>
            {areaLayers === 'counting' ? (
              <p className="px-3 py-2 text-gray-400 italic">Checking layers…</p>
            ) : areaLayers.length === 0 ? (
              <p className="px-3 py-2 text-gray-400 italic">No layers with attachments have features in this area.</p>
            ) : (
              <div className="py-1">
                {areaLayers.map((l) => (
                  <div key={l.id} className="px-3 py-1.5 border-b last:border-b-0" style={{ borderColor: '#142236' }}>
                    <label className="flex items-center gap-2 cursor-pointer text-gray-200">
                      <input
                        type="checkbox"
                        checked={l.included}
                        onChange={() => toggleLayer(l.id)}
                        disabled={exporting}
                        className="accent-gold-500"
                      />
                      <span className={`flex-1 truncate ${l.included ? '' : 'line-through text-gray-500'}`} title={l.title}>
                        {l.title}
                        <span className="block text-[10px] text-gray-500 uppercase">{l.geometryType}</span>
                      </span>
                      <span className="text-gray-500">{l.count}</span>
                    </label>
                    {l.included && (
                      <div className="mt-1 ml-6">
                        <span className="text-[10px] text-gray-500 uppercase tracking-wide">Folder by</span>
                        <select
                          value={l.groupField}
                          onChange={(e) => setLayerGroupField(l.id, e.target.value)}
                          disabled={exporting || !l.fieldChoices.length}
                          className="w-full mt-0.5 rounded px-1.5 py-1 text-[11px] text-gray-200 border focus:outline-none"
                          style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
                        >
                          <option value="">None — flat, no subfolders</option>
                          {l.fieldChoices.map((f) => (
                            <option key={f.name} value={f.name}>{f.alias}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <p className="text-gray-600 text-xs leading-relaxed">
        The zip is foldered by layer, then by utility type — <span className="text-gray-400">Gas</span>,
        <span className="text-gray-400"> Electric</span>, <span className="text-gray-400">Water</span> — read from each layer's
        type field and decoded through its subtypes and domains. The tool guesses that field per layer; use
        <span className="text-gray-400"> Folder by</span> to correct it. Features with no value land in
        <span className="text-gray-400"> Unspecified</span>. A <span className="text-gray-400">manifest.csv</span> lists every
        file, its type, and anything that failed to download. Only layers with attachments enabled appear in the panel —
        points and lines are checked by default, polygons are listed but left off. Type a
        <span className="text-gray-400"> P-number</span> to jump the map to that job's boundary (drawn in gold); when the
        boundary is a polygon, <span className="text-gray-400">Use Boundary as Area</span> makes it the export area so nothing
        has to be drawn by hand. <span className="text-gray-400">Export CSV</span> writes the checked layers' features in the
        area to a spreadsheet instead — pick the fields per layer; one layer downloads a single .csv, several download a
        zip with one .csv each.
      </p>
    </div>
  );
}
